import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Headphones, CheckCircle, Users, TrendingUp, MessageSquare, Star } from 'lucide-react';

const CustomerService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What should customer service professionals include in their performance review?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Include customer satisfaction scores (CSAT), first contact resolution rates, average handle time improvements, ticket volume handled, escalation prevention, and customer retention wins. Quantify impact: 'Maintained 4.8/5.0 CSAT rating across 2,400 interactions while reducing average handle time by 18%.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do customer service reps demonstrate value beyond ticket metrics?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document process improvements suggested, knowledge base contributions, peer coaching, complex issue resolution, customer saves, and feedback provided to product teams. Show how you've made the team and product better, not just your individual ticket numbers."
        }
      },
      {
        "@type": "Question",
        "name": "What metrics matter for customer support performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key metrics include: CSAT/NPS scores, first contact resolution rate, average handle time, tickets per hour, quality assurance scores, and customer effort score. Also track customer saves, upsells influenced, and feedback loop contributions."
        }
      },
      {
        "@type": "Question",
        "name": "How often should customer service reps track their achievements?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Track weekly, capturing positive customer feedback, complex resolutions, and process improvements as they happen. Log customer compliments and tough saves immediately. Use a tool like BragBox to capture wins in real-time so review preparation is easy."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Customer Service Professionals | BragBox</title>
        <meta name="description" content="Ace your customer service performance review with actionable tips. Learn how to showcase customer satisfaction wins, resolution skills, and contributions beyond ticket counts." />
        <meta name="keywords" content="customer service performance review, support rep self-evaluation, CSAT metrics, customer support career growth, call center review tips" />
        <link rel="canonical" href="https://bragbox.ai/tips/customer-service" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <Headphones className="w-4 h-4" />
              Customer Service Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Customer Service Professionals
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              You're the voice of the company, turning frustrated customers into loyal advocates. Learn how to document your impact beyond ticket counts and showcase the real value you bring.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why Customer Service Pros Struggle with Self-Evaluations</h2>
            <p className="text-gray-300 mb-6">
              You talked an angry customer off the ledge and turned them into a brand advocate. You spotted a product bug that was causing dozens of support tickets. You trained two new hires while maintaining your own queue. But when review time comes, your manager just looks at tickets closed per hour.
            </p>
            <p className="text-gray-300">
              Customer service work is often reduced to speed metrics when the real value is in quality, retention, and problem prevention. The customer who stayed because of your exceptional service? That's recurring revenue. The bug you flagged? That saved engineering hours and customer frustration. Your impact is bigger than your average handle time.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your Customer Service Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Track Customer Satisfaction Scores</h3>
                    <p className="text-gray-300 mb-3">
                      CSAT and NPS scores are your headline metrics. Document your personal scores, trends, and how you compare to team averages.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Maintained 4.9/5.0 CSAT rating across 3,200 customer interactions (team average: 4.5). NPS promoter rate of 78% vs team average of 62%. Received 89 written customer compliments specifically mentioning exceptional service."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document First Contact Resolution</h3>
                    <p className="text-gray-300 mb-3">
                      Solving issues on the first contact saves customer time and company resources. Track your FCR rate and improvements.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Achieved 87% first contact resolution rate (team average: 72%). Developed troubleshooting checklist that improved team FCR by 12% when adopted. Reduced average customer effort score from 3.8 to 2.4."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Highlight Customer Saves and Retention</h3>
                    <p className="text-gray-300 mb-3">
                      Every customer you save from churning is real revenue retained. Document cancellation prevention wins and at-risk customer turnarounds.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Prevented 23 customer cancellations through proactive outreach and issue resolution, retaining $68K in annual recurring revenue. Turned 8 detractor-score customers into promoters through exceptional follow-up."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Track Knowledge Base Contributions</h3>
                    <p className="text-gray-300 mb-3">
                      Articles you write or improve help customers self-serve and teammates resolve issues faster. Document your contributions.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Authored 12 knowledge base articles with 45K combined views. Top article reduced related ticket volume by 34%. Updated 28 existing articles based on customer feedback, improving help center satisfaction score from 3.2 to 4.1."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Document Product Feedback Contributions</h3>
                    <p className="text-gray-300 mb-3">
                      You hear customer pain points daily. Track bugs reported, feature requests submitted, and product improvements influenced.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Identified and reported 15 product bugs, 8 prioritized for immediate fix. Submitted 23 feature requests based on customer feedback, 5 added to roadmap. Bug I flagged was causing 40+ tickets weekly before resolution."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Track Training and Mentorship</h3>
                    <p className="text-gray-300 mb-3">
                      Helping teammates succeed multiplies your impact. Document new hires trained, peer coaching provided, and team knowledge shared.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Trained 4 new team members, all achieving independent status in 3 weeks (vs 5 week average). Conducted 12 peer coaching sessions on difficult customer scenarios. Created training documentation adopted for all new hire onboarding."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Highlight Complex Issue Resolution</h3>
                    <p className="text-gray-300 mb-3">
                      Some issues require skill beyond basic troubleshooting. Document escalations you resolved, edge cases you figured out, and technical issues you solved.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Resolved 45 escalated cases with 95% customer satisfaction. Became team expert on integration issues, reducing escalation rate for this category by 60%. Documented resolution for complex billing scenario, preventing future escalations."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Document Process Improvement Ideas</h3>
                    <p className="text-gray-300 mb-3">
                      Frontline perspective is valuable for improving workflows. Track process changes you suggested and their impact.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Suggested canned response improvements that reduced average handle time by 45 seconds across team. Identified workflow bottleneck in refund process, now resolved saving 3 minutes per refund request. Process improvement ideas saved estimated 40 team hours monthly."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Track Quality Assurance Scores</h3>
                    <p className="text-gray-300 mb-3">
                      QA scores measure the quality of your interactions. Document scores, trends, and areas where you've shown growth.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Achieved 96% average QA score (team average: 88%). Zero critical errors across 120 audited interactions. Improved empathy score from 85% to 98% after focused coaching, now consistently top performer in this category."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Log Wins in Real-Time</h3>
                    <p className="text-gray-300 mb-3">
                      Customer service moves fast. Document positive feedback, tough saves, and great interactions immediately.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After every 5-star review, customer compliment, or difficult issue resolved, take 30 seconds to log it in BragBox. Copy the customer's exact words when they praise you. When review time comes, you'll have a year of wins ready to share.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Customer Service Pros Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Customer Satisfaction</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• CSAT / satisfaction score</li>
                  <li>• NPS (promoter rate)</li>
                  <li>• Customer effort score</li>
                  <li>• Written compliments received</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Efficiency</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• First contact resolution rate</li>
                  <li>• Average handle time</li>
                  <li>• Tickets per hour</li>
                  <li>• Queue wait time contribution</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Quality</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• QA audit scores</li>
                  <li>• Escalation rate</li>
                  <li>• Reopen rate</li>
                  <li>• Accuracy metrics</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Impact</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Customer saves / retention</li>
                  <li>• Knowledge base contributions</li>
                  <li>• Product feedback submitted</li>
                  <li>• Team training provided</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What should customer service professionals include in their performance review?</h3>
                <p className="text-gray-300">Include customer satisfaction scores (CSAT), first contact resolution rates, average handle time improvements, ticket volume handled, escalation prevention, and customer retention wins. Quantify impact: "Maintained 4.8/5.0 CSAT rating across 2,400 interactions while reducing average handle time by 18%."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do customer service reps demonstrate value beyond ticket metrics?</h3>
                <p className="text-gray-300">Document process improvements suggested, knowledge base contributions, peer coaching, complex issue resolution, customer saves, and feedback provided to product teams. Show how you've made the team and product better, not just your individual ticket numbers.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What metrics matter for customer support performance reviews?</h3>
                <p className="text-gray-300">Key metrics include: CSAT/NPS scores, first contact resolution rate, average handle time, tickets per hour, quality assurance scores, and customer effort score. Also track customer saves, upsells influenced, and feedback loop contributions.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should customer service reps track their achievements?</h3>
                <p className="text-gray-300">Track weekly, capturing positive customer feedback, complex resolutions, and process improvements as they happen. Log customer compliments and tough saves immediately. Use a tool like BragBox to capture wins in real-time so review preparation is easy.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Your Customer Magic Deserves Recognition</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps customer service professionals capture CSAT wins, customer saves, and feedback contributions throughout the year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default CustomerService;
